import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MemberSelectorComponent } from '@shared/components/member-selector/member-selector.component';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { CreateSpiritualAssistanceVisitRequest, SpiritualAssistanceVisit, SpiritualAssistanceVisitApiService, SpiritualAssistanceVisitLocationType } from '@core/api/services/spiritual-assistance-visit-api.service';
import { GlobalSearchPersonResultDto } from '@core/api/services/search-api.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { NotificationService } from '@shared/services/notification.service';
import { PeopleApiService } from '@core/api/services/people-api.service';

interface MissingMembershipHistoryPerson { personId: string; fullName: string; currentOrganizationUnitName?: string | null; }

@Component({ selector: 'app-spiritual-assistance-visit-create', standalone: true, imports: [CommonModule, RouterModule, ReactiveFormsModule, MatButtonModule, MatCardModule, MatCheckboxModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatRadioModule, MatTooltipModule, DatePickerComponent, MemberSelectorComponent], templateUrl: './spiritual-assistance-visit-create.component.html', styleUrl: './spiritual-assistance-visits.component.scss' })
export class SpiritualAssistanceVisitCreateComponent implements OnInit {
  visitId: string | null = null;
  saving = false;
  loading = false;
  missingMembershipHistoryPeople: MissingMembershipHistoryPerson[] = [];
  selectedRecipients: GlobalSearchPersonResultDto[] = [];
  selectedVisitors: GlobalSearchPersonResultDto[] = [];
  showMemberCommunity = false;
  readonly form;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly api: SpiritualAssistanceVisitApiService,
    private readonly dialog: MatDialog,
    private readonly dateAdapter: DateAdapter<unknown>,
    private readonly cdr: ChangeDetectorRef,
    private readonly userInfo: UserFullInfoService,
    private readonly notifications: NotificationService,
    private readonly peopleApi: PeopleApiService,
  ) {
    this.form = this.fb.nonNullable.group({
      visitDate: [this.today(), Validators.required],
      locationType: this.fb.nonNullable.control<SpiritualAssistanceVisitLocationType>('Home'),
      locationDescription: ['', Validators.maxLength(200)],
      holyCommunionDelivered: [false],
      recipientIds: [[] as string[], Validators.required],
      visitorIds: [[] as string[], Validators.required],
    });
  }

  ngOnInit(): void {
    this.visitId = this.route.snapshot.paramMap.get('visit-id');
    this.showMemberCommunity = (this.userInfo.getUserInfoSnapshot()?.readableUnitIds?.length ?? 0) > 1;
    this.form.controls.locationType.valueChanges.subscribe(() => this.syncLocationDescriptionRules());
    this.syncLocationDescriptionRules();
    if (this.visitId) this.loadVisit(this.visitId);
    else {
      const recipientId = this.route.snapshot.queryParamMap.get('recipientId');
      if (recipientId) this.loadInitialRecipient(recipientId);
    }
  }

  get isEditMode(): boolean { return this.visitId !== null; }
  get title(): string { return this.isEditMode ? 'Editar VAE' : 'Registrar VAE'; }
  get backLink(): string[] { return this.isEditMode ? ['../..'] : ['..']; }
  get visitorEligibilityDate(): string { return this.toIsoDate(this.form.controls.visitDate.value); }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.missingMembershipHistoryPeople = [];
    if (this.isEditMode) {
      this.update();
      return;
    }

    this.api.preview(this.request()).subscribe({
      next: groups => {
        if (groups.length <= 1) {
          this.create();
          return;
        }

        this.dialog.open(ConfirmDialogComponent, modalDialogConfig({
          data: {
            title: 'Registrar VAE en varias comunidades',
            message: `Se crearán ${groups.length} VAE, una por cada comunidad involucrada. ¿Deseás continuar?`,
            confirmText: 'Registrar VAE',
            cancelText: 'Cancelar',
          },
          size: 'sm',
          layout: 'confirm',
        })).afterClosed().subscribe(confirmed => {
          if (confirmed) this.create();
        });
      },
      error: err => this.setError(err, 'No se pudo validar la VAE.'),
    });
  }

  updateRecipients(items: GlobalSearchPersonResultDto[]): void { this.selectedRecipients = items; this.missingMembershipHistoryPeople = []; }
  updateVisitors(items: GlobalSearchPersonResultDto[]): void { this.selectedVisitors = this.sortVisitors(items); }
  removeRecipient(id: string): void { this.setRecipients(this.selectedRecipients.filter(item => item.id !== id)); }
  removeVisitor(id: string): void { this.setVisitors(this.selectedVisitors.filter(item => item.id !== id)); }

  private loadInitialRecipient(id: string): void {
    this.loading = true;
    this.peopleApi.getById(id).subscribe({
      next: person => {
        this.setRecipients([{
          id: person.id ?? id,
          fullName: person.fullName?.trim() || [person.firstName, person.lastName].filter(Boolean).join(' '),
          membershipUnitId: person.membershipUnitId ?? '',
          membershipUnitName: person.membershipUnitName ?? 'Sin comunidad',
          documentNumber: person.documentNumber
        }]);
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        this.loading = false;
        this.setError(err, 'No se pudo cargar el miembro de la ficha. Podés seleccionarlo manualmente.');
      }
    });
  }

  private loadVisit(id: string): void {
    this.loading = true;
    this.api.getById(id).subscribe({
      next: visit => {
        this.selectedRecipients = this.toSearchPeople(visit.recipients);
        this.selectedVisitors = this.toSearchPeople(visit.visitors);
        this.form.patchValue({
          visitDate: this.toPickerDate(visit.visitDate),
          locationType: visit.locationType,
          locationDescription: visit.locationDescription ?? '',
          holyCommunionDelivered: visit.holyCommunionDelivered,
          recipientIds: visit.recipients.map(person => person.id),
          visitorIds: visit.visitors.map(person => person.id),
        }, { emitEvent: false });
        this.syncLocationDescriptionRules();
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: err => {
        this.loading = false;
        this.setError(err, 'No se pudo cargar la VAE.');
      },
    });
  }

  private create(): void {
    this.saving = true;
    this.api.create(this.request()).subscribe({ next: () => this.navigateToList(), error: err => this.setError(err, 'No se pudo registrar la VAE.', true) });
  }

  private update(): void {
    this.saving = true;
    this.api.update(this.visitId!, this.request()).subscribe({ next: () => this.navigateToList(), error: err => this.setError(err, 'No se pudo actualizar la VAE.', true) });
  }

  private navigateToList(): void { this.router.navigate(this.backLink, { relativeTo: this.route }); }
  private setRecipients(items: GlobalSearchPersonResultDto[]): void { this.selectedRecipients = items; this.missingMembershipHistoryPeople = []; this.form.controls.recipientIds.setValue(items.map(item => item.id)); }
  private setVisitors(items: GlobalSearchPersonResultDto[]): void { this.selectedVisitors = this.sortVisitors(items); this.form.controls.visitorIds.setValue(this.selectedVisitors.map(item => item.id)); }

  private sortVisitors(items: GlobalSearchPersonResultDto[]): GlobalSearchPersonResultDto[] {
    return [...items].sort((first, second) => {
      const firstOrder = first.ministrySortOrder ?? -1;
      const secondOrder = second.ministrySortOrder ?? -1;
      return secondOrder - firstOrder || first.fullName.localeCompare(second.fullName, 'es');
    });
  }

  private request(): CreateSpiritualAssistanceVisitRequest {
    const raw = this.form.getRawValue();
    return { ...raw, visitDate: this.toIsoDate(raw.visitDate), locationDescription: raw.locationType === 'Other' ? raw.locationDescription.trim() || null : null };
  }

  private syncLocationDescriptionRules(): void {
    const control = this.form.controls.locationDescription;
    if (this.form.controls.locationType.value === 'Other') {
      control.setValidators([Validators.required, Validators.maxLength(200)]);
    } else {
      control.setValidators([Validators.maxLength(200)]);
      control.setValue('', { emitEvent: false });
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  private toSearchPeople(people: SpiritualAssistanceVisit['recipients']): GlobalSearchPersonResultDto[] {
    return people.map(person => ({
      id: person.id,
      fullName: person.fullName,
      membershipUnitId: person.membershipUnitId ?? '',
      membershipUnitName: person.membershipUnitName ?? 'Sin comunidad',
    }));
  }

  private today(): unknown { const now = new Date(); return this.dateAdapter.createDate(now.getFullYear(), now.getMonth(), now.getDate()); }
  private toPickerDate(value: string): unknown { const [year, month, day] = value.split('-').map(Number); return this.dateAdapter.createDate(year, month - 1, day); }
  private toIsoDate(value: unknown): string { return `${this.dateAdapter.getYear(value)}-${String(this.dateAdapter.getMonth(value) + 1).padStart(2, '0')}-${String(this.dateAdapter.getDate(value)).padStart(2, '0')}`; }
  private setError(err: any, fallback: string, resetSaving = false): void {
    this.missingMembershipHistoryPeople = err.error?.missingMembershipHistoryPeople ?? [];
    if (!this.missingMembershipHistoryPeople.length) {
      this.notifications.error(err.error?.detail ?? fallback);
    }
    if (resetSaving) this.saving = false;
    this.cdr.markForCheck();
  }
}
